import { IconShare } from '@picsart/icons';
import IconChevronDownLarge from '@picsart/icons/IconChevronDownLarge';
import IconCrossCircleOutline from '@picsart/icons/IconCrossCircleOutline';
import { isMobile } from 'react-device-detect';
import { useState, useEffect } from 'react';
import cls from 'classnames';

import useCopyOnClick from '../../../../hooks/useCopyOnClick';

import EditorFooter from './EditorFooter';
import { EditorProps } from './types';
import useStyles from './styles';
import { useEditor } from './useEditor';
import { dataContainesAsciiArt } from './helpers';

function Editor({
  className,
  heading,
  data,
  setData,
  defaultText,
  onCloseClick,
  onCopyClick,
  onCopyError,
  onLinkButtonClick,
  expanded,
  onExpand,
  onShare,
  analytics,
}: EditorProps) {
  const classes = useStyles();
  const [text] = data;
  const hasText = text.length > 0;
  const textareaWithAscii = dataContainesAsciiArt(data);
  const [mobile, setMobile] = useState(isMobile);
  const [textareaRef, onPaste, onKeyDown, onChange, onClean] = useEditor({
    data,
    setData,
    onCloseClick,
    analytics,
  });
  const [_, copy] = useCopyOnClick(text, onCopyClick, onCopyError);

  const share = async (e) => {
    e.stopPropagation();
    if (!hasText) {
      return;
    }
    copy();

    let success = false;

    try {
      if (navigator.share) {
        await navigator.share({ text });
        success = true;
      }
    } catch (err) {
      console.warn('Error sharing');
    }
    onShare && onShare(success);
    analytics.pageClick({
      page_path: window.location.pathname,
      action_type: 'share_click',
    });
  };

  const handleExpand = (expand: boolean) => {
    onExpand(expand);
    analytics.pageClick({
      page_path: window.location.pathname,
      action_type: 'editor_icon_click',
    });
  };

  useEffect(() => {
    setMobile(isMobile);
  }, []);

  return (
    <div className={cls(classes.root, expanded && classes.expanded, className)}>
      <div className={classes.headerContainer} onClick={() => handleExpand(!expanded)}>
        <div className={classes.headerLeft}>
          <IconChevronDownLarge
            className={cls(classes.toggleIcon, !expanded && classes.toggleIconUp)}
          />
          <h3 className={classes.heading}>{heading}</h3>
        </div>

        {mobile && (
          <IconShare
            className={cls(classes.shareIcon, !hasText && classes.shareIconDisabled)}
            onClick={share}
          />
        )}
      </div>
      <div className={classes.textareaContainer}>
        <textarea
          id="area"
          ref={textareaRef}
          className={cls(classes.textarea, textareaWithAscii && classes.textareaWithAscii)}
          placeholder={defaultText}
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </div>
      {hasText && (
        <div className={classes.close} onClick={onClean}>
          <IconCrossCircleOutline className={classes.closeIcon} name="close-circle-gen" />
        </div>
      )}
      <EditorFooter
        analytics={analytics}
        data={data}
        onCopyClick={onCopyClick}
        onCopyError={onCopyError}
        onLinkButtonClick={onLinkButtonClick}
        copyShareDisabled={!hasText}
        setData={setData}
      />
    </div>
  );
}

export default Editor;
